import clsx from 'clsx'
import moment from 'moment'
import React, {FC, useEffect, useRef, useState} from 'react'
import {Badge, OverlayTrigger, Spinner, Tooltip} from 'react-bootstrap'
import {Link, useParams} from 'react-router-dom'
import useAppToast from 'src/hooks/useAppToast'
import {BillingPlan, UserSubscriptionPlan} from 'src/interfaces/Platform.interface'
import {useLayout} from 'src/marketConnect/layout/core'
import {fetchUserAccountDetailsAsync} from 'src/redux/actions/accountActions'
import {
  cancelSubscriptionAsync,
  getAllPlansAsync,
  getBillingPlatformId,
  getUserPlanAsync,
  requestFreePlanForWoocom,
  requestSubscriptionAsync,
  updateSubscriptionAsync,
} from 'src/redux/actions/pricingActions'
import {setStepsData} from 'src/redux/slices/tourPopupSlice'
import {useAppDispatch, useAppSelector} from 'src/redux/storeHooks'
import Loader from 'src/utils/Loader'
import CommonAlertModal from 'src/utils/modals/CommonAlertModal'
import './PricingPage.css'
import BillingPlanDetail from '../BillingPlanDetail'
import ToggleSwitch from 'src/utils/ToggleSwitch'
import InvoiceDetailsGeneration from './InvoiceDetails'
import PayPalModal from './PaypalPaymentModal'
import {formatBillingDateTimeUtc} from './billingDateFormat'
import { usePermission } from 'src/hooks/usePermission'
import { useFreeTrialDeferredOnboarding } from 'src/hooks/useFreeTrialDeferredOnboarding'
import { isDeferredFreeTrialOnboardingUser } from 'src/utils/freeTrialDeferredOnboarding'
import {
  changeSubscriptionPayload,
  requestSubscriptionPayload,
  selectApiForUpgradeWithSubscriptionId,
  shouldUsePaypalForCustomPlan,
  shouldUsePaypalForUpgradeSource,
} from './freeForeverUpgradeApi'
import {
  getPlanCardFeatureCount,
  getPlanCardFeatureHeading,
  isGenericFreePlanRecord,
  isMeaningfulPlanDescription,
  isRecommendedBillingPlan,
  PLAN_CARD_COLLAPSED_FEATURE_COUNT,
} from './planUsagePricing.helpers'

type PricingPlanType = 'STANDARD' | 'ENTERPRISE' | 'ENTERPRISE_PLUS'
type PlansLoadStatus = 'idle' | 'loading' | 'success' | 'error'

const canShowCancelSubscription = (plan: {
  trialPeriodDays?: number
  display_name?: string
  plan_name?: string
  description?: string
}) => {
  const planLabel = String(plan?.display_name || plan?.plan_name || '').trim()
  return (
    (plan.trialPeriodDays ?? 0) > 0 ||
    (planLabel === 'Custom Plan' && plan.description !== 'BILLED_MANUALLY')
  )
}

//After started Custom plan scenario
type UpdatedPricingPageV2Props = {
  onContinueWithFreeTrial?: () => void
  /** Renders only the production plan/payment block (used by Plan & Usage page). */
  planSectionOnly?: boolean
  /** Hides generic Free plan from upgrade list; keeps Free Trial plan rows. */
  excludeFreePlanFromUpgradeList?: boolean
  /** Suppresses "Upgrade/Choose Your Plan" heading when embedded in Plan & Usage. */
  hidePlanSectionHeading?: boolean
  /** Hides listing-channels footer CTA for welcome users on Plan & Usage (main parity). */
  isPlanAndUsageWelcomeUser?: boolean
}

const UpdatedPricingPageV2: FC<UpdatedPricingPageV2Props> = ({
  onContinueWithFreeTrial,
  planSectionOnly = false,
  excludeFreePlanFromUpgradeList = false,
  hidePlanSectionHeading = false,
  isPlanAndUsageWelcomeUser: isPlanAndUsageWelcomeUserProp = false,
}) => {
  const {isRead, isWrite, isDelete} = usePermission();
  const [billingPeriod, setBillingPeriod] = useState<'month' | 'annual'>('month')
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null)
  const [listOfPlans, setListOfPlans] = useState<any[]>([])
  const [userSubscribedPlan, setUserSubscriptionPlan] = useState<UserSubscriptionPlan>()
  const currentSubscriptionId = userSubscribedPlan?.current_subscription_id
    ? String(userSubscribedPlan.current_subscription_id).trim()
    : null
  const {mainStoreData} = useAppSelector((state) => state.fetchConnectedPlatform)
  let {platformId} = useParams()
  platformId = platformId ? platformId : `${mainStoreData?.id}`
  const dispatch = useAppDispatch()
  const {successToast, warningToast, errorToast} = useAppToast()
  const [isLoading, setIsLoading] = useState(false)
  const [isCancelSubscriptionConfirm, setIsCancelSubscriptionConfirm] = useState<boolean>(false)
  const [isCanceling, setIsCanceling] = useState<boolean>(false)
  const [isWixDowngrading, setIsWixDowngrading] = useState(false)
  const [isWixCancelling, setIsWixCancelling] = useState(false)
  const [confirmDowngradeToFree, setConfirmDowngradeToFree] = useState(false)
  const [isPlanClicked, setIsPlanClicked] = useState(false)
  const [downgradingPlanId, setDowngradingPlanId] = useState<number | null>(null)
  const [upgradingPlanId, setUpgradingPlanId] = useState<number | null>(null)
  const [trialingPlanId, setTrialingPlanId] = useState<number | null>(null)
  const {fetchedUserAccountData, fetchedUserAccountLoading} = useAppSelector((state) => state.userAccountData)
  const {userCurrentSubscription} = useAppSelector((state) => state.pricing)
  const {openOnboardingWizard} = useFreeTrialDeferredOnboarding()
  const [showMsgForCustomPlanUser, setShowMsgForCustomPlanUser] = useState<boolean>(false)
  const [activePlanDetail, setActivePlanDetail] = useState<UserSubscriptionPlan | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [isAnnual, setIsAnnual] = useState<boolean | null>(false);
  const [planType, setPlanType] = useState<PricingPlanType>('STANDARD');
  const [plansLoadStatus, setPlansLoadStatus] = useState<PlansLoadStatus>('idle')
  const [loadedPlanType, setLoadedPlanType] = useState<PricingPlanType | null>(null)
  const [expandedPlanId, setExpandedPlanId] = useState<number | null>(null)
  const planRequestIdRef = useRef(0)
  const isPlanTypeSet = useRef(false);
  const isSallaSource = fetchedUserAccountData?.source === 'SALLA' // Salla users manage billing in Salla
  // Trailing '_' = prefix match (e.g. SHOPIFY_ → SHOPIFY_ODOO, SHOPIFY_WIX, SHOPIFY_BC).
  const shouldUsePaypalForSource = shouldUsePaypalForUpgradeSource(fetchedUserAccountData?.source)
  const shouldUsePaypalForPlan = (plan: any, isCustomPlan: boolean) => {
    if (!isCustomPlan) return shouldUsePaypalForSource
    return shouldUsePaypalForCustomPlan(plan?.source)
  }
  const getSallaTooltip = (action: string) => (isSallaSource ? `Go to Salla to ${action}` : undefined)
  const sallaTooltipId = useRef(0)
  const wrapWithSallaTooltip = (node: JSX.Element, title?: string) =>
    isSallaSource && title ? (
      <OverlayTrigger
        placement='top'
        delay={{show: 50, hide: 0}} // Faster show, immediate hide to match existing tooltips
        overlay={<Tooltip id={`salla-tooltip-${++sallaTooltipId.current}`}>{title}</Tooltip>}
      >
        <span className='d-inline-block'>{node}</span>
      </OverlayTrigger>
    ) : (
      node
    )
  const featureTooltipId = useRef(0)
  const decodeHtmlEntities = (text: string) =>
    text
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
  const wrapWithFeatureTooltip = (node: JSX.Element, description?: string) =>
    description ? (
      <OverlayTrigger
        placement='top'
        delay={{show: 50, hide: 0}}
        overlay={
          <Tooltip
            id={`feature-tooltip-${++featureTooltipId.current}`}
            style={{fontSize: '12px', maxWidth: '320px', whiteSpace: 'normal'}}
          >
            {decodeHtmlEntities(description)}
          </Tooltip>
        }
      >
        <span className='d-flex flex-grow-1 pe-2'>{node}</span>
      </OverlayTrigger>
    ) : (
      <span className='d-flex flex-grow-1 pe-2'>{node}</span>
    )
  const [billingPlatformId, setBillingPlatformId] = useState(null)
  const [isOldPrice, setIsOldPrice] = useState(false)
  const [activeTab, setActiveTab] = useState<'PLAN' | 'TRANSACTIONS'>('PLAN');
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedPlan, setSelectedPlan] = useState<any>({})
  const [loaded, setLoaded] = useState(false)

  const handleBillingPeriodChange = (period) => {
    setBillingPeriod(period)
    // Reset selected plan when billing period changes
  }

  const fetchBillingPlatformId = () => {
    dispatch(getBillingPlatformId({})).then((response)=> setBillingPlatformId(response.payload.data))
  }

  // Check user has any current active billing plan
  useEffect(() => {
    setLoading(true)
    dispatch(getUserPlanAsync())
      .then((response) => {
        if (response?.payload?.data?.billing_plan) {
          setActivePlanDetail(response.payload?.data)
        }
        if (response.payload?.status === 204) {
          setActivePlanDetail(null)
        }
        if (response.payload?.status === 500) {
          warningToast(response.payload?.data?.error)
        }
        setLoading(false)
      })
      .catch(() => {
        setLoading(false)
      })

      fetchBillingPlatformId()
  }, [])

  useEffect(() => {
    const requestedPlanType = planType
    const requestId = ++planRequestIdRef.current
    let isCurrentRequest = true
    const isLatestRequest = () => isCurrentRequest && planRequestIdRef.current === requestId

    setIsLoading(true)
    setPlansLoadStatus('loading')
    setLoadedPlanType(null)
    setListOfPlans([])

    const plansRequest = dispatch(
      getAllPlansAsync({user_id: fetchedUserAccountData?.id!, type: requestedPlanType})
    )
      .then((response) => {
        if (!isLatestRequest()) {
          return
        }

        if (getAllPlansAsync.fulfilled.match(response) && Array.isArray(response.payload)) {
          setListOfPlans(response.payload)
          setLoadedPlanType(requestedPlanType)
          setPlansLoadStatus('success')
        } else {
          console.error('Expected an array for listOfPlans:', response.payload)
          setListOfPlans([])
          setLoadedPlanType(null)
          setPlansLoadStatus('error')
        }
      })
      .catch((error) => {
        if (!isLatestRequest()) {
          return
        }

        console.error('Failed to load billing plans:', error)
        setListOfPlans([])
        setLoadedPlanType(null)
        setPlansLoadStatus('error')
      })

    const userPlanRequest = dispatch(getUserPlanAsync()).then((response) => {
        if (!isLatestRequest()) {
          return
        }

        if (response.payload?.data?.billing_plan) {
          setUserSubscriptionPlan({
            ...response.payload?.data,
            user_count: response.payload?.user_count,
          })

          // Check Old Price if Current Billing Plan exists
          const {user_transactions, current_billing_plan, billing_plan} = response.payload?.data
           const isOldPrice = current_billing_plan === user_transactions?.billing_plan && billing_plan?.price?.value !== Number(user_transactions?.amount_paid) ? true : false
           setIsOldPrice(isOldPrice)

          const {billing_frequency, plan_type} = response.payload?.data?.billing_plan
          let active_plan_type = (plan_type === 'COMMON' ? 'STANDARD' : plan_type) as PricingPlanType

          if (!isPlanTypeSet.current) {
            setPlanType(active_plan_type);
            isPlanTypeSet.current = true;
          }
          if (billing_frequency === 'ANNUAL'){
            if(planType === active_plan_type ){
              setBillingPeriod('annual')
              setIsAnnual(true)}
          }
          if (billing_frequency === 'EVERY_30_DAYS'){
            if(planType === active_plan_type ){
              setBillingPeriod('month')
              setIsAnnual(false)}
          }
        }
        if (response.payload?.status === 204) {
          //will not get user billing plan
          setUserSubscriptionPlan(undefined)
          // warningToast(response.payload?.data?.error)
        }
        if (response.payload?.status === 500) {
          warningToast(response.payload?.data?.error)
        }
      })

    Promise.allSettled([plansRequest, userPlanRequest]).finally(() => {
      if (isLatestRequest()) {
        setIsLoading(false)
      }
    })

    return () => {
      isCurrentRequest = false
    }
  }, [planType])
 

  // Load PayPal SDK
  useEffect(() => {
    if (!document.querySelector('script[src*=\"paypal.com/sdk/js\"]')) {
      const script = document.createElement('script')
      script.src =
        process.env['REACT_APP_ENV'] === 'staging'
          ? 'https://www.sandbox.paypal.com/sdk/js?client-id=AaIKeo-ubwFTKv2lAUqORl2Al3RWDttmSNUkxaBB53sdNUGZYVIAxzaKexdqHxxKYY3w7z13pGtsZmNY&components=buttons&vault=true&intent=subscription'
          : 'https://www.paypal.com/sdk/js?client-id=AYXOgcDre2F0DiXJ4VB0SPMUpmbgp7W53BMEpZMmVxDH7-JVbrHHcKI7KjqAeU-kZbzKmW_RDQ6c_Rzx&components=buttons&vault=true&intent=subscription'
      script.async = true
      script.onload = () => setLoaded(true)
      document.body.appendChild(script)
    } else {
      setLoaded(true)
    }
  }, [])

  const filteredPlans =
    billingPeriod === 'annual'
      ? listOfPlans.filter(
          (plan) => plan.billing_frequency === 'ANNUAL' || plan.billing_frequency === 'FREE_FOREVER'
        )
      : listOfPlans.filter(
          (plan) =>
            plan.billing_frequency === 'EVERY_30_DAYS' || plan.billing_frequency === 'FREE_FOREVER'
        )


  const isNewFreeTrialOnboardingUser = isDeferredFreeTrialOnboardingUser(
    fetchedUserAccountData,
    userSubscribedPlan ?? userCurrentSubscription
  )
  const isPlanContentReady = plansLoadStatus === 'success' && loadedPlanType === planType
  const shouldShowPlansLoader =
    plansLoadStatus === 'loading' ||
    plansLoadStatus === 'idle' ||
    (plansLoadStatus === 'success' && loadedPlanType !== planType) ||
    (isLoading && !isPlanContentReady)

  const calculatePrice = (plan: BillingPlan) => {
    let price: any;
    if(userSubscribedPlan?.current_billing_plan === plan?.id){
      price = isOldPrice ? Number(userSubscribedPlan?.user_transactions?.amount_paid) : plan.price.value
    } else {
      price = plan.price.value
    }
    if (plan.billing_frequency === 'ANNUAL') {
      const discount = 0.2 // 20% discount
      const discountedPrice = plan.price.value * (1 - discount)
      // return discountedPrice // Format to two decimal places
      return price
    } else {
      return price
    }
  }


  // if (isLoading) return <Loader />

  const handleSwitch = (tab: 'month' | 'annual') => {
    setBillingPeriod(tab)
  }

  const setBillingPeriodChoice = (period: 'month' | 'annual') => {
    handleSwitch(period)
    setIsAnnual(period === 'annual')
  }

  const planTypeTabs: {type: PricingPlanType; label: string; subtitle: string}[] = [
    {type: 'STANDARD', label: 'Standard', subtitle: 'Standard plans'},
    {type: 'ENTERPRISE', label: 'Enterprise', subtitle: 'Enterprise plans'},
    {type: 'ENTERPRISE_PLUS', label: 'ERP', subtitle: 'ERP plans'},
  ]

  const handleTabChange = (tab: PricingPlanType) => {
    if (tab === planType && plansLoadStatus === 'success') {
      return
    }

    setPlansLoadStatus('loading')
    setLoadedPlanType(null)
    setListOfPlans([])
    setPlanType(tab)
  }

  useEffect(() => {
    if (!planSectionOnly) {
      return
    }
    setExpandedPlanId(null)
  }, [billingPeriod, planType, loadedPlanType, planSectionOnly])
  return (
    <>
      <div
        id={planSectionOnly ? 'pricing-plans-section' : undefined}
        className='card'
        style={{borderRadius: '5px !important'}}
      >
        {!planSectionOnly ? (
          <div
            className='d-flex mb-2'
            style={{width: '100%', justifyContent: 'center'}}
          >
            <ul
              className='nav nav-stretch nav-line-tabs nav-line-tabs-2x border-transparent fs-5 fw-bolder flex-nowrap my-1'
              style={{
                width: '100%',
                justifyContent: 'space-between',
              }}
            >
              <li
                className='nav-item'
                style={{flex: 1, textAlign: 'center', paddingLeft: '15px'}}
              >
                <button
                  className={
                    activeTab === 'PLAN'
                      ? 'nav-link active-tab text-active-primary text-secondary'
                      : 'nav-link border-bottom text-secondary'
                  }
                  onClick={() => setActiveTab('PLAN')}
                  style={{
                    width: '100%',
                    fontSize: '14px',
                    fontWeight: '500',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    textAlign: 'center',
                  }}
                >
                  Plans
                </button>
              </li>

              <li
                className='nav-item'
                style={{flex: 1, textAlign: 'center', paddingRight: '15px'}}
              >
                <button
                  className={
                    activeTab === 'TRANSACTIONS'
                      ? 'nav-link active-tab text-active-primary text-secondary'
                      : 'nav-link border-bottom text-secondary'
                  }
                  onClick={() => setActiveTab('TRANSACTIONS')}
                  style={{
                    width: '100%',
                    fontSize: '15px',
                    fontWeight: '600',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    textAlign: 'center',
                  }}
                >
                  Transactions
                </button>
              </li>
            </ul>
          </div>
        ) : null}

        {(planSectionOnly || activeTab === 'PLAN') && (
          <>
        {!planSectionOnly && (loading || fetchedUserAccountLoading) ? (
          <Loader />
        ) : !planSectionOnly && activePlanDetail ? (
          <BillingPlanDetail
            activePlanDetail={activePlanDetail}
            isOldPrice={isOldPrice}
          />
        ) : null}

        {!planSectionOnly && isNewFreeTrialOnboardingUser ? (
          <div className='free-trial-banner mb-5'>
            <div className='free-trial-banner__content'>
              <p className='free-trial-banner__title'>
                Your Free Trial is Active — You can start using Commercium now, or choose a plan below to upgrade.
              </p>

              <button
                type='button'
                className='free-trial-banner__button'
                onClick={onContinueWithFreeTrial ?? openOnboardingWizard}
              >
                Continue with Free Trial
                <span className='free-trial-banner__arrow' aria-hidden='true'>
                  →
                </span>
              </button>
            </div>
          </div>
        ) : null}

        {/* <div className='separator mb-1'></div> */}
        {/* <div className='card'> */}
        <div
          className={clsx('card-body py-3 px-2', planSectionOnly && 'pu-available-plans__body')}
        >
          <div className={clsx('pricing-tab-container', planSectionOnly && 'pu-available-plans')}>
            {!hidePlanSectionHeading ? (
              <div className='text-center'>
                <h1 className='fs-3 mb-2'>{activePlanDetail ? 'Upgrade' : 'Choose'} Your Plan</h1>
              </div>
            ) : null}
            {planSectionOnly ? (
              <div className='pu-available-plans__controls'>
                <div className='pu-available-plans__header'>
                  <div className='pu-available-plans__header-copy'>
                    <h2 id='pu-available-plans-heading' className='pu-available-plans__heading'>
                      Available plans
                    </h2>
                    <p className='pu-available-plans__subtitle'>
                      Switch plans any time. Your data and connections carry over.
                    </p>
                  </div>
                  <div className='pu-available-plans__billing'>
                    <span id='pu-billing-cycle-label' className='pu-available-plans__field-label'>
                      Billing cycle
                    </span>
                    <div
                      className='pu-available-plans__billing-segment'
                      role='group'
                      aria-labelledby='pu-billing-cycle-label'
                    >
                      <button
                        type='button'
                        className={clsx('pu-available-plans__billing-option', {
                          'pu-available-plans__billing-option--active': billingPeriod === 'month',
                        })}
                        aria-pressed={billingPeriod === 'month'}
                        onClick={() => setBillingPeriodChoice('month')}
                      >
                        Monthly
                      </button>
                      <button
                        type='button'
                        className={clsx('pu-available-plans__billing-option', {
                          'pu-available-plans__billing-option--active': billingPeriod === 'annual',
                        })}
                        aria-pressed={billingPeriod === 'annual'}
                        onClick={() => setBillingPeriodChoice('annual')}
                      >
                        Annual
                        <span className='pu-available-plans__billing-badge'>2 months free</span>
                      </button>
                    </div>
                  </div>
                </div>
                <div className='pu-available-plans__type'>
                  <span id='pu-plan-type-label' className='pu-available-plans__field-label'>
                    Plan type
                  </span>
                  <div
                    className='pu-available-plans__categories'
                    role='tablist'
                    aria-labelledby='pu-plan-type-label'
                  >
                    {planTypeTabs.map(({type, label, subtitle}) => (
                      <button
                        key={type}
                        type='button'
                        role='tab'
                        aria-selected={planType === type}
                        className={clsx('pu-available-plans__category', {
                          'pu-available-plans__category--active': planType === type,
                        })}
                        onClick={() => handleTabChange(type)}
                      >
                        <span className='pu-available-plans__category-title'>
                          <span className='pu-available-plans__category-radio' aria-hidden='true' />
                          {label}
                        </span>
                        <span className='pu-available-plans__category-subtitle'>{subtitle}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className='psp-tabs mt-4'>
                  <button
                    className={`psp-tab ${planType === 'STANDARD' ? 'psp-active' : ''}`}
                    onClick={() => handleTabChange('STANDARD')}
                  >
                    Standard
                  </button>
                  <button
                    className={`psp-tab ${planType === 'ENTERPRISE' ? 'psp-active' : ''}`}
                    onClick={() => handleTabChange('ENTERPRISE')}
                  >
                    Enterprise
                  </button>

                  <button
                    className={`psp-tab ${planType === 'ENTERPRISE_PLUS' ? 'psp-active' : ''}`}
                    onClick={() => handleTabChange('ENTERPRISE_PLUS')}
                  >
                    ERP
                  </button>
                </div>
                <ToggleSwitch
                  handleSwitch={handleSwitch}
                  activePlanDetail={activePlanDetail}
                  isAnnual={isAnnual}
                  setIsAnnual={setIsAnnual}
                />
              </>
            )}
            {shouldShowPlansLoader ? (
              <Loader />
            ) : plansLoadStatus === 'error' ? (
              <div className='content'>
                <div className='row row-cols-2 row-cols-lg-auto g-2 g-lg-2 g-10 gx-3 flex-wrap justify-content-center mt-1'>
                  <div className='card shadow'>
                    <div className='card-body'>
                      <h3>Unable to load plans. Please reload the page once.</h3>
                    </div>
                  </div>
                </div>
              </div>
            ) : isPlanContentReady ? (
              <div className='content'>
              {/* {billingPeriod === 'month' ? (
                        <div className="plan-details">
                        <h3>month Plan</h3>
                        <p>$10/month</p>
                        <p>Includes basic features.</p>
                        </div>
                    ) : (
                        <div className="plan-details">
                        <h3>annual Plan</h3>
                        <p>$100/year</p>
                        <p>Save 20% with an annual subscription.</p>
                        </div>
                    )} */}
              <div
                className={clsx(
                  planSectionOnly
                    ? 'pu-available-plans__grid'
                    : 'row row-cols-2 row-cols-lg-auto g-2 g-lg-2 g-10 gx-3 flex-wrap justify-content-center mt-1'
                )}
              >
                {filteredPlans?.length ? (
                  filteredPlans?.map((plan) => {
                    const isFreePlan =
                      plan?.billing_frequency === 'FREE_FOREVER' || Number(plan?.price?.value) === 0
                    const planDisplayName = String(plan?.display_name || plan?.plan_name || '').trim()
                    const parentPlanName =
                      typeof plan?.parent === 'string'
                        ? plan.parent.trim()
                        : String(plan?.parent?.display_name || plan?.parent?.plan_name || plan?.parent?.name || '').trim()
                    const isFreeTrialPlan =
                      plan?.billing_frequency === 'FREE_FOREVER' &&
                      plan?.plan_type === 'COMMON' &&
                      planDisplayName.toLowerCase() === 'free trial - 28 days'
                    const sallaAction = userSubscribedPlan?.isActive ? 'Upgrade' : 'Subscribe'
                    const isCustomPlan = plan?.price?.type === 'CUSTOM_PLAN'
                    const planUserId = (plan as any)?.user?.id
                    // only show custom plans that belong to the logged-in user (planUserId match)
                    if (isCustomPlan && planUserId && planUserId !== fetchedUserAccountData?.id) {
                      return null
                    }
                    if (excludeFreePlanFromUpgradeList && isGenericFreePlanRecord(plan)) {
                      return null
                    } else {
                      const isCurrentPlan =
                        userSubscribedPlan?.current_billing_plan === plan.id &&
                        userSubscribedPlan?.isActive
                      const planDescription = String(plan?.description ?? '').trim()
                      const isDisplayOnlyCustomPlanSubtitle =
                        isCustomPlan &&
                        planDescription.replace(/\s+/g, ' ').trim().toLowerCase() ===
                          'this is a display only custom plan'
                      const showPlanDescription =
                        planSectionOnly &&
                        isMeaningfulPlanDescription(planDescription, plan) &&
                        !isDisplayOnlyCustomPlanSubtitle
                      const isFeaturesExpanded = planSectionOnly && expandedPlanId === plan.id
                      const featureCount = getPlanCardFeatureCount(plan)
                      const showFeaturesToggle =
                        planSectionOnly && featureCount > PLAN_CARD_COLLAPSED_FEATURE_COUNT
                      const isRecommendedPlan = planSectionOnly && isRecommendedBillingPlan(plan)

                      return (
                        <div
                          className={clsx(
                            planSectionOnly
                              ? 'pu-available-plans__card-col'
                              : 'col-xl-2 col-lg-3 col-md-3 col-sm-6 col-12 my-2'
                          )}
                          style={planSectionOnly ? undefined : {minHeight: '300px', minWidth: '230px'}}
                          key={plan.id}
                        >
                          <div className='d-flex h-100'>
                            <div
                              className={clsx(
                                planSectionOnly
                                  ? 'pu-available-plans__card'
                                  : isCurrentPlan
                                  ? 'w-100 d-flex flex-column rounded-3 bg-white bg-opacity-105 p-4 border border-2 border-primary shadow-lg bg-hover-light-primary'
                                  : 'w-100 d-flex flex-column  shadow rounded-3 bg-white bg-opacity-105 p-4 bg-hover-light-primary',
                                planSectionOnly &&
                                  isCurrentPlan &&
                                  'pu-available-plans__card--current',
                                planSectionOnly &&
                                  isFeaturesExpanded &&
                                  'pu-available-plans__card--expanded',
                                !planSectionOnly && 'w-100 d-flex flex-column'
                              )}
                            >
                              <div
                                className={clsx(
                                  'my-1 text-center w-100',
                                  planSectionOnly && 'pu-available-plans__card-head'
                                )}
                                style={planSectionOnly ? undefined : {minHeight: '60px'}}
                              >
                                {planSectionOnly ? (
                                  <div className='pu-available-plans__card-badge-slot'>
                                    {isCurrentPlan ? (
                                      <span className='pu-available-plans__your-plan-badge'>
                                        Your plan
                                      </span>
                                    ) : isRecommendedPlan ? (
                                      <span className='pu-available-plans__popular-badge'>
                                        Most popular
                                      </span>
                                    ) : null}
                                  </div>
                                ) : null}
                                <h3
                                  className={clsx(
                                    planSectionOnly
                                      ? 'pu-available-plans__card-title'
                                      : 'fs-5 text-gray-900 fw-bolder p-2'
                                  )}
                                >
                                  {plan.display_name}
                                </h3>
                                {planSectionOnly && showPlanDescription ? (
                                  <p className='pu-available-plans__card-desc'>{planDescription}</p>
                                ) : null}
                                {isFreeTrialPlan ? (
                                  <div
                                    className={clsx(
                                      planSectionOnly
                                        ? 'pu-available-plans__card-note'
                                        : 'text-gray-900 fw-semibold'
                                    )}
                                    style={planSectionOnly ? undefined : {fontSize: '14px', lineHeight: 1}}
                                  >
                                    (No CC Required)
                                  </div>
                                ) : null}
                              </div>
                              <div
                                className={clsx(
                                  'mb-2 text-center',
                                  planSectionOnly && 'pu-available-plans__card-price-wrap'
                                )}
                                style={planSectionOnly ? undefined : {minHeight: '20px'}}
                              >
                                <div
                                  className={clsx(
                                    'text-center align-items-center d-flex justify-content-center',
                                    planSectionOnly && 'pu-available-plans__card-price'
                                  )}
                                >
                                  <span
                                    className={clsx(
                                      planSectionOnly ? 'pu-available-plans__price-currency' : 'text-primary fs-2'
                                    )}
                                    style={
                                      planSectionOnly
                                        ? undefined
                                        : {
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            whiteSpace: 'nowrap',
                                          }
                                    }
                                  >
                                    { plan?.price?.value > 0
                                      ? plan?.price?.currency_iso === 'USD'
                                        ? '$'
                                        : '₹'
                                      : ' '}
                                  </span>
                                  <span
                                    className={clsx(
                                      planSectionOnly
                                        ? 'pu-available-plans__price-value'
                                        : 'fw-bold text-primary lh-1'
                                    )}
                                    style={planSectionOnly ? undefined : {fontSize: '30px'}}
                                  >
                                    {plan?.price?.type === 'CUSTOM_PLAN' ? (
                                      plan?.price?.value
                                    ) : plan?.price?.value > 0 ? (
                                      calculatePrice(plan)?.toFixed(2)
                                    ) : (
                                      'Free'
                                    )}
                                  </span>
                                  <span
                                    className={clsx(
                                      planSectionOnly
                                        ? 'pu-available-plans__price-period'
                                        : 'fs-8 fw-bold text-dark opacity-50'
                                    )}
                                  >
                                    {plan?.price?.value > 0
                                      ? billingPeriod === 'annual'
                                        ? planSectionOnly
                                          ? '/ year'
                                          : '/Year'
                                        : planSectionOnly
                                        ? '/ month'
                                        : '/Month'
                                      : null}
                                  </span>
                                </div>
                                {planSectionOnly ? (
                                  <span className='pu-available-plans__price-billed'>
                                    {Number(plan?.price?.value) > 0
                                      ? billingPeriod === 'annual'
                                        ? 'Billed annually'
                                        : 'Billed monthly'
                                      : null}
                                  </span>
                                ) : null}
                                { userSubscribedPlan?.billing_plan?.billing_frequency !== 'FREE_FOREVER' && userSubscribedPlan?.current_billing_plan === plan.id && isOldPrice && <div className="badge text-danger rounded-pill px-3 py-1 text-center" style={{backgroundColor: '#fabfcd'}}>OLD PRICE</div>}

                              </div>
                              <div
                                className={clsx('my-3', planSectionOnly && 'pu-available-plans__card-features')}
                              >
                                {planSectionOnly ? (
                                  <div
                                    className={clsx('pu-available-plans__feat-head', {
                                      'pu-available-plans__feat-head--base': !parentPlanName,
                                    })}
                                  >
                                    <svg
                                      width='16'
                                      height='16'
                                      viewBox='0 0 24 24'
                                      fill='none'
                                      stroke='currentColor'
                                      strokeWidth='2.25'
                                      strokeLinecap='round'
                                      strokeLinejoin='round'
                                      aria-hidden='true'
                                    >
                                      <path d='m12 2 9 5-9 5-9-5Z' />
                                      <path d='m3 12 9 5 9-5' />
                                      <path d='m3 17 9 5 9-5' />
                                    </svg>
                                    <span>{getPlanCardFeatureHeading(parentPlanName)}</span>
                                  </div>
                                ) : (
                                  <>
                                    <div
                                      className='fw-bold fs-6 text-gray-900 mb-3 mt-0'
                                      style={{fontSize: '1.4rem', lineHeight: 1}}
                                    >
                                      Features include:
                                    </div>
                                    {parentPlanName ? (
                                      <div className='psp-parent-badge'>
                                        <span className='psp-parent-badge-icon'>
                                          <i className='fa-solid fa-star' />
                                        </span>
                                        <span className='psp-parent-badge-text text-truncate'>
                                          {parentPlanName} &amp;
                                        </span>
                                      </div>
                                    ) : null}
                                  </>
                                )}
                                <div
                                  className={clsx(
                                    planSectionOnly && 'pu-available-plans__features-list',
                                    showFeaturesToggle &&
                                      !isFeaturesExpanded &&
                                      'pu-available-plans__features-list--collapsed',
                                    isFeaturesExpanded && 'pu-available-plans__features-list--expanded'
                                  )}
                                >
                                {Array.isArray(plan?.billingplanfeature_set) &&
                                plan.billingplanfeature_set.length > 0 ? (
                                  <>
                                    {plan.billingplanfeature_set.map((feature, index) => {
                                      const isActive = Boolean(feature?.isActive ?? feature?.is_active)
                                      const featureName = feature?.feature_name ?? feature?.name ?? ''
                                      const featureDescription = feature?.description ?? ''

                                      return (
                                        <div
                                          key={feature?.id ?? index}
                                          className='d-flex align-items-center justify-content-between mb-2'
                                        >
                                          {wrapWithFeatureTooltip(
                                            <span className='fw-semibold fs-6 text-gray-800'>{featureName}</span>,
                                            featureDescription
                                          )}
                                          {isActive ? (
                                            <i className='ki-duotone ki-check-circle fs-3 text-success'>
                                              <span className='path1'></span>
                                              <span className='path2'></span>
                                            </i>
                                          ) : (
                                            <i className='ki-duotone ki-cross-circle fs-3 text-danger'>
                                              <span className='path1'></span>
                                              <span className='path2'></span>
                                            </i>
                                          )}
                                        </div>
                                      )
                                    })}
                                  </>
                                ) : (
                                  <>
                                <div className='d-flex align-items-center mb-2'>
                                  <span className='fw-semibold fs-6 text-gray-800 flex-grow-1 pe-1'>
                                    {plan?.order_limit} Orders/Month
                                  </span>
                                  <i className='ki-duotone ki-check-circle fs-3 text-success'>
                                    <span className='path1'></span>
                                    <span className='path2'></span>
                                  </i>
                                </div>
                                <div className='d-flex align-items-center mb-2'>
                                  <span className='fw-semibold fs-6 text-gray-800 flex-grow-1 pe-1'>
                                    {plan?.product_limit} SKUs
                                  </span>
                                  <i className='ki-duotone ki-check-circle fs-3 text-success'>
                                    <span className='path1'></span>
                                    <span className='path2'></span>
                                  </i>
                                </div>
                                <div className='d-flex align-items-center mb-2'>
                                  <span className='fw-semibold fs-6 text-gray-800 flex-grow-1 pe-1'>
                                    Unlimited Channel Integration
                                  </span>
                                  {plan?.isUnlimitedChannelIntegrationAllowed ? (
                                    <i className='ki-duotone ki-check-circle fs-3 text-success'>
                                      <span className='path1'></span>
                                      <span className='path2'></span>
                                    </i>
                                  ) : (
                                    <i className='ki-duotone ki-cross-circle fs-3 text-danger'>
                                      <span className='path1'></span>
                                      <span className='path2'></span>
                                    </i>
                                  )}
                                </div>
                                <div className='d-flex align-items-center mb-2'>
                                  <span className='fw-semibold fs-6 text-gray-800 flex-grow-1 pe-1'>
                                    Inventory Sync
                                  </span>
                                  {plan?.isInventorySyncAllowed ? (
                                    <i className='ki-duotone ki-check-circle fs-3 text-success'>
                                      <span className='path1'></span>
                                      <span className='path2'></span>
                                    </i>
                                  ) : (
                                    <i className='ki-duotone ki-cross-circle fs-3 text-danger'>
                                      <span className='path1'></span>
                                      <span className='path2'></span>
                                    </i>
                                  )}
                                </div>
                                <div className='d-flex align-items-center mb-2'>
                                  <span className='fw-semibold fs-6 text-gray-800 flex-grow-1 pe-1'>
                                    Product Listings
                                  </span>
                                  {plan?.isProductListingsAllowed ? (
                                    <i className='ki-duotone ki-check-circle fs-3 text-success'>
                                      <span className='path1'></span>
                                      <span className='path2'></span>
                                    </i>
                                  ) : (
                                    <i className='ki-duotone ki-cross-circle fs-3 text-danger'>
                                      <span className='path1'></span>
                                      <span className='path2'></span>
                                    </i>
                                  )}
                                </div>
                                <div className='d-flex align-items-center mb-2'>
                                  <span className='fw-semibold fs-6 text-gray-800 flex-grow-1 pe-1'>
                                    Dedicated Account Manager
                                  </span>
                                  {plan?.isDedicatedAccountManagerAllowed ? (
                                    <i className='ki-duotone ki-check-circle fs-3 text-success'>
                                      <span className='path1'></span>
                                      <span className='path2'></span>
                                    </i>
                                  ) : (
                                    <i className='ki-duotone ki-cross-circle fs-3 text-danger'>
                                      <span className='path1'></span>
                                      <span className='path2'></span>
                                    </i>
                                  )}
                                </div>
                                <div className='d-flex align-items-center mb-2'>
                                  <span className='fw-semibold fs-6 text-gray-800 flex-grow-1 pe-1'>
                                    Live Chat Support
                                  </span>
                                  {plan?.isLiveChatSupportAllowed ? (
                                    <i className='ki-duotone ki-check-circle fs-3 text-success'>
                                      <span className='path1'></span>
                                      <span className='path2'></span>
                                    </i>
                                  ) : (
                                    <i className='ki-duotone ki-cross-circle fs-3 text-danger'>
                                      <span className='path1'></span>
                                      <span className='path2'></span>
                                    </i>
                                  )}
                                </div>
                                <div className='d-flex align-items-center mb-2'>
                                  <span className='fw-semibold fs-6 text-gray-800 flex-grow-1 pe-1'>
                                    Email Support
                                  </span>
                                  {plan?.isEmailSupportAllowed ? (
                                    <i className='ki-duotone ki-check-circle fs-3 text-success'>
                                      <span className='path1'></span>
                                      <span className='path2'></span>
                                    </i>
                                  ) : (
                                    <i className='ki-duotone ki-cross-circle fs-3 text-danger'>
                                      <span className='path1'></span>
                                      <span className='path2'></span>
                                    </i>
                                  )}
                                </div>
                                <div className='d-flex align-items-center mb-2'>
                                  <span className='fw-semibold fs-6 text-gray-800 flex-grow-1 pe-1'>
                                    Near Realtime Sync
                                  </span>
                                  {plan?.isNearRealtimeSyncAllowed ? (
                                    <i className='ki-duotone ki-check-circle fs-3 text-success'>
                                      <span className='path1'></span>
                                      <span className='path2'></span>
                                    </i>
                                  ) : (
                                    <i className='ki-duotone ki-cross-circle fs-3 text-danger'>
                                      <span className='path1'></span>
                                      <span className='path2'></span>
                                    </i>
                                  )}
                                </div>
                                  </>
                                )}
                                </div>
                                {showFeaturesToggle ? (
                                  <button
                                    type='button'
                                    className='pu-available-plans__features-toggle'
                                    aria-expanded={isFeaturesExpanded}
                                    onClick={() =>
                                      setExpandedPlanId((current) =>
                                        current === plan.id ? null : plan.id
                                      )
                                    }
                                  >
                                    {isFeaturesExpanded
                                      ? 'Show fewer'
                                      : `Show all ${featureCount} features`}
                                  </button>
                                ) : null}
                              </div>

                              {isWrite && (<div className={clsx('d-flex flex-wrap justify-content-center flex-column mt-auto align-items-center', planSectionOnly && 'pu-available-plans__card-actions')}>
                                {(userSubscribedPlan?.cancelled_at &&
                                  userSubscribedPlan.current_billing_plan !== plan?.id) ||
                                userSubscribedPlan == undefined ||
                                userSubscribedPlan.cancelled_at === null ? (
                                  <>
                                    {userSubscribedPlan?.current_billing_plan === plan?.id &&
                                    userSubscribedPlan?.isActive ? (
                                      planSectionOnly ? (
                                        <>
                                          <div
                                            className='pu-available-plans__current-plan-btn'
                                            aria-current='true'
                                          >
                                            <i className='ki-duotone ki-check fs-4'>
                                              <span className='path1'></span>
                                              <span className='path2'></span>
                                            </i>
                                            Current plan
                                          </div>
                                          {userSubscribedPlan.billing_plan.description !==
                                            'BILLED_MANUALLY' &&
                                            userSubscribedPlan.cancelled_at === null &&
                                            canShowCancelSubscription(plan) &&
                                            wrapWithSallaTooltip(
                                              <button
                                                type='button'
                                                className='pu-available-plans__cancel-link'
                                                disabled={
                                                  isPlanClicked ||
                                                  fetchedUserAccountData?.role
                                                    ?.manage_plan_access === 'READ' ||
                                                  fetchedUserAccountData?.role
                                                    ?.manage_plan_access === 'RESTRICT' ||
                                                  isSallaSource
                                                }
                                                title={getSallaTooltip('Cancel Subscription')}
                                                style={{
                                                  pointerEvents: isSallaSource ? 'none' : 'auto',
                                                }}
                                                onClick={() => {
                                                  if (
                                                    ['WIX'].includes(
                                                      fetchedUserAccountData?.source!
                                                    )
                                                  ) {
                                                    setIsWixCancelling(true)
                                                  } else {
                                                    setIsCancelSubscriptionConfirm(true)
                                                  }
                                                }}
                                              >
                                                Cancel subscription
                                              </button>,
                                              getSallaTooltip('Cancel Subscription')
                                            )}
                                        </>
                                      ) : (
                                      <button
                                        disabled
                                        className='bg-primary border-0 rounded pt-1 text-white fw-bold px-4'
                                        style={{paddingBottom: '5px'}}
                                      >
                                        Subscribed
                                      </button>
                                      )
                                    ) : plan?.price?.type !== 'CUSTOM_PLAN' ? (
                                    plan?.price?.value >=
                                      Number(userSubscribedPlan?.billing_plan?.price?.value) ||
                                    userSubscribedPlan == undefined ||
                                    !userSubscribedPlan?.isActive ? (
                                      wrapWithSallaTooltip(
                                          <button
                                          className='btn btn-sm btn-primary shadow-lg fw-bold fs-6 px-3'
                                          style={{
                                            paddingBottom: '8px',
                                            paddingTop: '6px',
                                            pointerEvents: isSallaSource && !isFreePlan ? 'none' : 'auto', // Let parent span handle tooltip hover for Salla
                                          }}
                                          disabled={
                                            isPlanClicked ||
                                            fetchedUserAccountData?.role?.manage_plan_access === "READ" ||
                                            fetchedUserAccountData?.role?.manage_plan_access === "RESTRICT" ||
                                            (isSallaSource && !isFreePlan) || // Disable subscribe/upgrade for paid Salla plans
                                            (isSallaSource && userSubscribedPlan?.isActive) // Disable upgrade for Salla
                                          }
                                          title={
                                            isSallaSource
                                              ? !isFreePlan
                                                ? getSallaTooltip(sallaAction)
                                                : userSubscribedPlan?.isActive
                                                ? getSallaTooltip(sallaAction)
                                                : undefined
                                              : undefined
                                          }
                                          onClick={() => {
                                            if (isSallaSource && !isFreePlan) return
                                            if (isCustomPlan && shouldUsePaypalForPlan(plan, isCustomPlan)) {
                                              setSelectedPlan(plan)
                                              setIsModalOpen(true)
                                              return
                                            }
                                            if (shouldUsePaypalForSource && plan.billing_frequency !== 'FREE_FOREVER') {
                                              setSelectedPlan(plan)
                                              setIsModalOpen(true)
                                              return
                                            }
                                            if (isFreePlan) {
                                              setSelectedPlanId(plan.plan_id)
                                              setConfirmDowngradeToFree(true)
                                              return
                                            }
                                            if (!currentSubscriptionId) {
                                              //if user is upgrading first time
                                              if (
                                                userSubscribedPlan?.activated_on == null &&
                                                userSubscribedPlan?.valid_till == null
                                              ) {
                                                setIsPlanClicked(true)
                                                setUpgradingPlanId(plan.id)
                                                dispatch(
                                                  requestSubscriptionAsync({
                                                    plan_id: plan.id,
                                                    platform_id: billingPlatformId ?? null,
                                                    // take_free_days_trial: plan.trialPeriodDays,
                                                  })
                                                )
                                                  .unwrap()
                                                  .then((response) => {
                                                    // Check if the response is successful
                                                    if (
                                                      response.status_code === 201 &&
                                                      response.data
                                                    ) {
                                                      // Automatically open the link in a new tab
                                                      // successToast(response?.massage)
                                                      window.open(response.data, '_self')
                                                      setTimeout(() => {
                                                        setIsPlanClicked(false)
                                                        setUpgradingPlanId(null)
                                                      }, 4000)
                                                    } else {
                                                      // Handle error or different status codes
                                                      console.error('Error response:', response)
                                                      warningToast(
                                                        'Something went wrong. Please try again later'
                                                      )
                                                      setIsPlanClicked(false)
                                                      setUpgradingPlanId(null)

                                                      // Optionally, show a user-friendly message or perform other actions
                                                    }
                                                  })
                                                  .catch((error) => {
                                                    // Handle any errors that occur during dispatch or response handling
                                                    console.error(
                                                      'Error during subscription request:',
                                                      error
                                                    )
                                                    if (
                                                      process.env['REACT_APP_ENV'] === 'staging'
                                                    ) {
                                                      warningToast(
                                                        'An error occurred ' +
                                                        error?.data?.massage
                                                      )
                                                    } else {
                                                      warningToast(
                                                        'An error occurred. Please try again later'
                                                      )
                                                    }
                                                    setIsPlanClicked(false)
                                                    setUpgradingPlanId(null)
                                                    // Optionally, show a user-friendly message or perform other actions
                                                  })
                                              } else {
                                                //request free plan
                                                if (plan?.billing_frequency === 'FREE_FOREVER') {
                                                  setSelectedPlanId(plan.plan_id)
                                                  setConfirmDowngradeToFree(true)
                                                } else {
                                                  // Dispatch requestSubscriptionAsync if no subscription_id
                                                  setIsPlanClicked(true)
                                                  setUpgradingPlanId(plan.id)
                                                  dispatch(
                                                    requestSubscriptionAsync({
                                                      plan_id: plan.id,
                                                      platform_id: billingPlatformId ?? null,
                                                      // take_free_days_trial: plan.trialPeriodDays,
                                                    })
                                                  )
                                                    .unwrap()
                                                    .then((response) => {
                                                      // Check if the response is successful
                                                      if (
                                                        response.status_code === 201 &&
                                                        response.data
                                                      ) {
                                                        // Automatically open the link in a new tab
                                                        // successToast(response?.massage)
                                                        window.open(response.data, '_self')
                                                        setTimeout(() => {
                                                          setIsPlanClicked(false)
                                                          setUpgradingPlanId(null)
                                                        }, 4000)
                                                      } else {
                                                        // Handle error or different status codes
                                                        console.error('Error response:', response)
                                                        warningToast(
                                                          'Something went wrong. Please try again later'
                                                        )
                                                        setIsPlanClicked(false)
                                                        setUpgradingPlanId(null)
                                                        // Optionally, show a user-friendly message or perform other actions
                                                      }
                                                    })
                                                    .catch((error) => {
                                                      // Handle any errors that occur during dispatch or response handling
                                                      console.error(
                                                        'Error during subscription request:',
                                                        error
                                                      )
                                                      if (
                                                        process.env['REACT_APP_ENV'] === 'staging'
                                                      ) {
                                                        warningToast(
                                                          'An error occurred ' +
                                                            error?.data?.massage
                                                        )
                                                      } else {
                                                        warningToast(
                                                          'An error occurred. Please try again later'
                                                        )
                                                      }
                                                      setIsPlanClicked(false)
                                                      setUpgradingPlanId(null)
                                                      // Optionally, show a user-friendly message or perform other actions
                                                    })
                                                }
                                              }
                                            } else {
                                              // Dispatch if subscription_id is present
                                              if (
                                                userSubscribedPlan == undefined &&
                                                plan?.billing_frequency === 'FREE_FOREVER'
                                              ) {
                                                setSelectedPlanId(plan.plan_id)
                                                setConfirmDowngradeToFree(true)
                                              } else {
                                                if (
                                                  userSubscribedPlan == undefined ||
                                                  (plan?.billing_frequency !== 'FREE_FOREVER' &&
                                                    !currentSubscriptionId)
                                                ) {
                                                  setIsPlanClicked(true)
                                                  setUpgradingPlanId(plan.id)
                                                  dispatch(
                                                    requestSubscriptionAsync({
                                                      plan_id: plan.id,
                                                      platform_id: billingPlatformId ?? null,
                                                      // take_free_days_trial: plan.trialPeriodDays,
                                                    })
                                                  )
                                                    .unwrap()
                                                    .then((response) => {
                                                      // Check if the response is successful
                                                      if (
                                                        response.status_code === 201 &&
                                                        response.data
                                                      ) {
                                                        // Automatically open the link in a new tab
                                                        // successToast(response?.massage)
                                                        window.open(response.data, '_self')
                                                        setTimeout(() => {
                                                          setIsPlanClicked(false)
                                                          setUpgradingPlanId(null)
                                                        }, 4000)
                                                      } else {
                                                        // Handle error or different status codes
                                                        console.error('Error response:', response)
                                                        // Optionally, show a user-friendly message or perform other actions
                                                        warningToast(
                                                          'Something went wrong. Please try again later'
                                                        )
                                                        setIsPlanClicked(false)
                                                        setUpgradingPlanId(null)
                                                      }
                                                    })
                                                    .catch((error) => {
                                                      // Handle any errors that occur during dispatch or response handling
                                                      console.error(
                                                        'Error during subscription request:',
                                                        error
                                                      )
                                                      if (
                                                        process.env['REACT_APP_ENV'] === 'staging'
                                                      ) {
                                                        warningToast(
                                                          'An error occurred ' +
                                                            error?.data?.massage
                                                        )
                                                      } else {
                                                        warningToast(
                                                          'An error occurred. Please try again later'
                                                        )
                                                      }
                                                      setIsPlanClicked(false)
                                                      setUpgradingPlanId(null)
                                                      // Optionally, show a user-friendly message or perform other actions
                                                    })
                                                } else {
                                                  const upgradeApi = selectApiForUpgradeWithSubscriptionId(
                                                    userSubscribedPlan?.billing_plan?.billing_frequency
                                                  )
                                                  setIsPlanClicked(true)
                                                  setUpgradingPlanId(plan.id)
                                                  const upgradeRequest =
                                                    upgradeApi === 'request-subscription'
                                                      ? dispatch(
                                                          requestSubscriptionAsync(
                                                            requestSubscriptionPayload(
                                                              plan.id,
                                                              billingPlatformId ?? null
                                                            )
                                                          )
                                                        )
                                                      : dispatch(
                                                          updateSubscriptionAsync(
                                                            changeSubscriptionPayload(
                                                              plan?.id,
                                                              billingPlatformId ?? null,
                                                              currentSubscriptionId
                                                            )
                                                          )
                                                        )
                                                  upgradeRequest
                                                    .unwrap()
                                                    .then((response) => {
                                                      // Check if the response is successful
                                                      if (
                                                        response?.status_code === 201 &&
                                                        response?.data
                                                      ) {
                                                        // successToast(response?.massage)
                                                        window.open(response.data, '_self')
                                                        setTimeout(() => {
                                                          setIsPlanClicked(false)
                                                          setUpgradingPlanId(null)
                                                        }, 4000)
                                                      } else {
                                                        // Handle error or different status codes

                                                        // errorToast(response?.error)
                                                        console.error('Error response:', response)
                                                        warningToast(
                                                          'Something went wrong. Please try again later'
                                                        )
                                                        setIsPlanClicked(false)
                                                        setUpgradingPlanId(null)
                                                        // Optionally, show a user-friendly message or perform other actions
                                                      }
                                                    })
                                                    .catch((error) => {
                                                      // Handle any errors that occur during dispatch or response handling
                                                      console.error(
                                                        'Error during subscription update:',
                                                        error
                                                      )
                                                      if (
                                                        process.env['REACT_APP_ENV'] === 'staging'
                                                      ) {
                                                        // warningToast('An error occurred ' + error?.data?.massage )
                                                        warningToast(error?.data?.massage)
                                                      } else {
                                                        // warningToast('An error occurred. Please try again later')
                                                        warningToast(error?.data?.massage)
                                                      }
                                                      setIsPlanClicked(false)
                                                      setUpgradingPlanId(null)
                                                      // Optionally, show a user-friendly message or perform other actions
                                                    })
                                                }
                                              }
                                            }
                                          }}
                                        >
                                          {upgradingPlanId === plan.id && isPlanClicked ? (
                                            <div className='min-w-100px'>
                                              <Spinner animation='border' size='sm' />
                                            </div>
                                          ) : userSubscribedPlan == undefined ||
                                            !userSubscribedPlan?.isActive ? (
                                            'Subscribe'
                                          ) : (
                                            'Upgrade'
                                          )}
                                          </button>,
                                          !isFreePlan
                                            ? getSallaTooltip(sallaAction)
                                            : userSubscribedPlan?.isActive
                                            ? getSallaTooltip(sallaAction)
                                            : undefined
                                        )
                                      ) : (
                                        wrapWithSallaTooltip(
                                          <button
                                            className='btn btn-sm btn-primary shadow px-3 mt-2 fw-bold fs-7 pt-1'
                                            style={{
                                              paddingBottom: '5px',
                                              pointerEvents: isSallaSource ? 'none' : 'auto', // Let parent span handle tooltip hover for Salla
                                            }}
                                            disabled={
                                              isPlanClicked ||
                                              fetchedUserAccountData?.role?.manage_plan_access === "READ" ||
                                              fetchedUserAccountData?.role?.manage_plan_access === "RESTRICT" ||
                                              isSallaSource // Disable downgrade for Salla
                                            }
                                            title={getSallaTooltip('Downgrade')}
                                            onClick={() => {
                                            if (isCustomPlan && shouldUsePaypalForPlan(plan, isCustomPlan)) {
                                              setSelectedPlan(plan)
                                              setIsModalOpen(true)
                                              return
                                            }
                                            if (shouldUsePaypalForSource && plan.billing_frequency !== 'FREE_FOREVER') {
                                              setSelectedPlan(plan)
                                              setIsModalOpen(true)
                                              return
                                            }
                                            if (
                                              ['WIX'].includes(fetchedUserAccountData?.source!)
                                            ) {
                                              if (userSubscribedPlan?.cancelled_at !== null) {
                                                if (plan.billing_frequency !== 'FREE_FOREVER') {
                                                  setIsPlanClicked(true)
                                                  setDowngradingPlanId(plan.id)
                                                  dispatch(
                                                    requestSubscriptionAsync({
                                                      plan_id: plan.id,
                                                      platform_id: billingPlatformId ?? null,
                                                      // take_free_days_trial: plan.trialPeriodDays,
                                                    })
                                                  )
                                                    .unwrap()
                                                    .then((response) => {
                                                      // Check if the response is successful
                                                      if (
                                                        response.status_code === 201 &&
                                                        response.data
                                                      ) {
                                                        // Automatically open the link in a new tab
                                                        // successToast(response?.massage)
                                                        window.open(response.data, '_self')
                                                        setTimeout(() => {
                                                          setIsPlanClicked(false)
                                                          setDowngradingPlanId(null)
                                                        }, 4000)
                                                      } else {
                                                        // Handle error or different status codes
                                                        console.error('Error response:', response)
                                                        warningToast(
                                                          'Something went wrong. Please try again later'
                                                        )
                                                        setIsPlanClicked(false)
                                                        setDowngradingPlanId(null)
                                                        // Optionally, show a user-friendly message or perform other actions
                                                      }
                                                    })
                                                    .catch((error) => {
                                                      // Handle any errors that occur during dispatch or response handling
                                                      console.error(
                                                        'Error during subscription request:',
                                                        error
                                                      )
                                                      if (
                                                        process.env['REACT_APP_ENV'] === 'staging'
                                                      ) {
                                                        warningToast(
                                                          'An error occurred ' +
                                                            error?.data?.massage
                                                        )
                                                      } else {
                                                        warningToast(
                                                          'U An error occurred. Please try again later'
                                                        )
                                                      }
                                                      setIsPlanClicked(false)
                                                      setDowngradingPlanId(null)
                                                      // Optionally, show a user-friendly message or perform other actions
                                                    })
                                                } else {
                                                  setSelectedPlanId(plan.plan_id)
                                                  setConfirmDowngradeToFree(true)
                                                }
                                              } else {
                                                setIsWixDowngrading(true)
                                              }
                                            } else {
                                              if (!currentSubscriptionId && plan?.billing_frequency !== 'FREE_FOREVER') {
                                                // Dispatch requestSubscriptionAsync if no subscription_id
                                                setIsPlanClicked(true)
                                                setDowngradingPlanId(plan.id)
                                                dispatch(
                                                  requestSubscriptionAsync({
                                                    plan_id: plan.id,
                                                    platform_id: billingPlatformId ?? null,
                                                    // take_free_days_trial: plan.trialPeriodDays,
                                                  })
                                                )
                                                  .unwrap()
                                                  .then((response) => {
                                                    // Check if the response is successful
                                                    if (
                                                      response.status_code === 201 &&
                                                      response.data
                                                    ) {
                                                      // Automatically open the link in a new tab
                                                      // successToast(response?.massage)
                                                      window.open(response.data, '_self')
                                                      setTimeout(() => {
                                                        setIsPlanClicked(false)
                                                        setDowngradingPlanId(null)
                                                      }, 4000)
                                                    } else {
                                                      // Handle error or different status codes
                                                      console.error('Error response:', response)
                                                      warningToast(
                                                        'Something went wrong. Please try again later'
                                                      )
                                                      setIsPlanClicked(false)
                                                      setDowngradingPlanId(null)
                                                      // Optionally, show a user-friendly message or perform other actions
                                                    }
                                                  })
                                                  .catch((error) => {
                                                    // Handle any errors that occur during dispatch or response handling
                                                    console.error(
                                                      'Error during subscription request:',
                                                      error
                                                    )
                                                    if (
                                                      process.env['REACT_APP_ENV'] === 'staging'
                                                    ) {
                                                      warningToast(
                                                        'An error occurred ' +
                                                        error?.data?.massage
                                                      )
                                                    } else {
                                                      warningToast(
                                                        'U An error occurred. Please try again later'
                                                      )
                                                    }
                                                    setIsPlanClicked(false)
                                                    setDowngradingPlanId(null)
                                                    // Optionally, show a user-friendly message or perform other actions
                                                  })
                                              } else {
                                                // Dispatch updateSubscriptionAsync if subscription_id is present
                                                if (plan?.billing_frequency === 'FREE_FOREVER') {
                                                  setSelectedPlanId(plan.plan_id)
                                                  setConfirmDowngradeToFree(true)
                                                } else {
                                                  setIsPlanClicked(true)
                                                  setDowngradingPlanId(plan.id)
                                                  dispatch(
                                                    updateSubscriptionAsync({
                                                      plan_id: plan?.id,
                                                      platform_id: Number(billingPlatformId) ?? null,
                                                      subscription_id: currentSubscriptionId,
                                                    })
                                                  )
                                                    .unwrap()
                                                    .then((response) => {
                                                      // Check if the response is successful
                                                      if (
                                                        response.status_code === 201 &&
                                                        response.data
                                                      ) {
                                                        // Automatically open the link in a new tab
                                                        window.open(response.data, '_self')
                                                        setTimeout(() => {
                                                          setIsPlanClicked(false)
                                                          setDowngradingPlanId(null)
                                                        }, 4000)
                                                      } else {
                                                        // Handle error or different status codes
                                                        console.error('Error response:', response)
                                                        warningToast(
                                                          'Something went wrong. Please try again later'
                                                        )
                                                        setIsPlanClicked(false)
                                                        setDowngradingPlanId(null)
                                                        // Optionally, show a user-friendly message or perform other actions
                                                      }
                                                    })
                                                    .catch((error) => {
                                                      // Handle any errors that occur during dispatch or response handling
                                                      console.error(
                                                        'Error during subscription update:',
                                                        error
                                                      )
                                                      if (
                                                        process.env['REACT_APP_ENV'] === 'staging'
                                                      ) {
                                                        warningToast(error?.data?.massage)
                                                      } else {
                                                        warningToast(error?.data?.massage)
                                                      }
                                                      setIsPlanClicked(false)
                                                      setDowngradingPlanId(null)
                                                      // Optionally, show a user-friendly message or perform other actions
                                                    })
                                                }
                                              }
                                            }
                                            }}
                                          >
                                            {downgradingPlanId === plan.id && isPlanClicked ? (
                                              <div className='min-w-100px'>
                                                <Spinner animation='border' size='sm' />
                                              </div>
                                            ) : (
                                              'Downgrade'
                                            )}
                                          </button>,
                                          getSallaTooltip('Downgrade')
                                        )
                                      )
                                    ) : plan?.price?.value > 0 ? (  //this line checking for common custom plan
                                      plan?.price?.value >=
                                        Number(userSubscribedPlan?.billing_plan?.price?.value) ||
                                      userSubscribedPlan == undefined ||
                                      !userSubscribedPlan?.isActive ? (
                                        wrapWithSallaTooltip(
                                          <button
                                            className='btn btn-sm btn-primary shadow-lg fw-bold fs-6 px-3'
                                            style={{
                                              paddingBottom: '8px',
                                              paddingTop: '6px',
                                              pointerEvents: isSallaSource && !isFreePlan ? 'none' : 'auto', // Let parent span handle tooltip hover for Salla
                                            }}
                                            disabled={
                                              isPlanClicked ||
                                              fetchedUserAccountData?.role?.manage_plan_access === "READ" ||
                                              fetchedUserAccountData?.role?.manage_plan_access === "RESTRICT" ||
                                              (isSallaSource && !isFreePlan) || // Disable subscribe/upgrade for paid Salla plans
                                              (isSallaSource && userSubscribedPlan?.isActive) // Disable upgrade for Salla
                                            }
                                            title={
                                              isSallaSource
                                                ? !isFreePlan
                                                  ? getSallaTooltip(
                                                      userSubscribedPlan?.isActive ? 'Upgrade' : 'Subscribe'
                                                    )
                                                  : userSubscribedPlan?.isActive
                                                  ? getSallaTooltip('Upgrade')
                                                  : undefined
                                                : undefined
                                          }
                                          onClick={() => {
                                            if (isSallaSource && !isFreePlan) return
                                            if (isCustomPlan && shouldUsePaypalForPlan(plan, isCustomPlan)) {
                                              setSelectedPlan(plan)
                                              setIsModalOpen(true)
                                              return
                                            }
                                            if (isFreePlan) {
                                              setSelectedPlanId(plan.plan_id)
                                              setConfirmDowngradeToFree(true)
                                              return
                                            }
                                            if (!currentSubscriptionId) {
                                              //if user is upgrading first time
                                              if (
                                                userSubscribedPlan?.activated_on == null &&
                                                userSubscribedPlan?.valid_till == null
                                              ) {
                                                setIsPlanClicked(true)
                                                setUpgradingPlanId(plan.id)
                                                dispatch(
                                                  requestSubscriptionAsync({
                                                    plan_id: plan.id,
                                                    platform_id: billingPlatformId ?? null,
                                                    // take_free_days_trial: plan.trialPeriodDays,
                                                  })
                                                )
                                                  .unwrap()
                                                  .then((response) => {
                                                    // Check if the response is successful
                                                    if (
                                                      response.status_code === 201 &&
                                                      response.data
                                                    ) {
                                                      // Automatically open the link in a new tab
                                                      // successToast(response?.massage)
                                                      window.open(response.data, '_self')
                                                      setTimeout(() => {
                                                        setIsPlanClicked(false)
                                                        setUpgradingPlanId(null)
                                                      }, 4000)
                                                    } else {
                                                      // Handle error or different status codes
                                                      console.error('Error response:', response)
                                                      warningToast(
                                                        'Something went wrong. Please try again later'
                                                      )
                                                      setIsPlanClicked(false)
                                                      setUpgradingPlanId(null)

                                                      // Optionally, show a user-friendly message or perform other actions
                                                    }
                                                  })
                                                  .catch((error) => {
                                                    // Handle any errors that occur during dispatch or response handling
                                                    console.error(
                                                      'Error during subscription request:',
                                                      error
                                                    )
                                                    if (
                                                      process.env['REACT_APP_ENV'] === 'staging'
                                                    ) {
                                                      warningToast(
                                                        'An error occurred ' +
                                                        error?.data?.massage
                                                      )
                                                    } else {
                                                      warningToast(
                                                        'An error occurred. Please try again later'
                                                      )
                                                    }
                                                    setIsPlanClicked(false)
                                                    setUpgradingPlanId(null)
                                                    // Optionally, show a user-friendly message or perform other actions
                                                  })
                                              } else {
                                                //request free plan
                                                if (plan?.billing_frequency === 'FREE_FOREVER') {
                                                  setSelectedPlanId(plan.plan_id)
                                                  setConfirmDowngradeToFree(true)
                                                } else {
                                                  // Dispatch requestSubscriptionAsync if no subscription_id
                                                  setIsPlanClicked(true)
                                                  setUpgradingPlanId(plan.id)
                                                  dispatch(
                                                    requestSubscriptionAsync({
                                                      plan_id: plan.id,
                                                      platform_id: billingPlatformId ?? null,
                                                      // take_free_days_trial: plan.trialPeriodDays,
                                                    })
                                                  )
                                                    .unwrap()
                                                    .then((response) => {
                                                      // Check if the response is successful
                                                      if (
                                                        response.status_code === 201 &&
                                                        response.data
                                                      ) {
                                                        // Automatically open the link in a new tab
                                                        // successToast(response?.massage)
                                                        window.open(response.data, '_self')
                                                        setTimeout(() => {
                                                          setIsPlanClicked(false)
                                                          setUpgradingPlanId(null)
                                                        }, 4000)
                                                      } else {
                                                        // Handle error or different status codes
                                                        console.error('Error response:', response)
                                                        warningToast(
                                                          'Something went wrong. Please try again later'
                                                        )
                                                        setIsPlanClicked(false)
                                                        setUpgradingPlanId(null)
                                                        // Optionally, show a user-friendly message or perform other actions
                                                      }
                                                    })
                                                    .catch((error) => {
                                                      // Handle any errors that occur during dispatch or response handling
                                                      console.error(
                                                        'Error during subscription request:',
                                                        error
                                                      )
                                                      if (
                                                        process.env['REACT_APP_ENV'] === 'staging'
                                                      ) {
                                                        warningToast(
                                                          'An error occurred ' +
                                                            error?.data?.massage
                                                        )
                                                      } else {
                                                        warningToast(
                                                          'An error occurred. Please try again later'
                                                        )
                                                      }
                                                      setIsPlanClicked(false)
                                                      setUpgradingPlanId(null)
                                                      // Optionally, show a user-friendly message or perform other actions
                                                    })
                                                }
                                              }
                                            } else {
                                              // Dispatch if subscription_id is present
                                              if (
                                                userSubscribedPlan == undefined &&
                                                plan?.billing_frequency === 'FREE_FOREVER'
                                              ) {
                                                setSelectedPlanId(plan.plan_id)
                                                setConfirmDowngradeToFree(true)
                                              } else {
                                                if (
                                                  userSubscribedPlan == undefined ||
                                                  (plan?.billing_frequency !== 'FREE_FOREVER' &&
                                                    !currentSubscriptionId)
                                                ) {
                                                  setIsPlanClicked(true)
                                                  setUpgradingPlanId(plan.id)
                                                  dispatch(
                                                    requestSubscriptionAsync({
                                                      plan_id: plan.id,
                                                      platform_id: billingPlatformId ?? null,
                                                      // take_free_days_trial: plan.trialPeriodDays,
                                                    })
                                                  )
                                                    .unwrap()
                                                    .then((response) => {
                                                      // Check if the response is successful
                                                      if (
                                                        response.status_code === 201 &&
                                                        response.data
                                                      ) {
                                                        // Automatically open the link in a new tab
                                                        // successToast(response?.massage)
                                                        window.open(response.data, '_self')
                                                        setTimeout(() => {
                                                          setIsPlanClicked(false)
                                                          setUpgradingPlanId(null)
                                                        }, 4000)
                                                      } else {
                                                        // Handle error or different status codes
                                                        console.error('Error response:', response)
                                                        // Optionally, show a user-friendly message or perform other actions
                                                        warningToast(
                                                          'Something went wrong. Please try again later'
                                                        )
                                                        setIsPlanClicked(false)
                                                        setUpgradingPlanId(null)
                                                      }
                                                    })
                                                    .catch((error) => {
                                                      // Handle any errors that occur during dispatch or response handling
                                                      console.error(
                                                        'Error during subscription request:',
                                                        error
                                                      )
                                                      if (
                                                        process.env['REACT_APP_ENV'] === 'staging'
                                                      ) {
                                                        warningToast(
                                                          'An error occurred ' +
                                                            error?.data?.massage
                                                        )
                                                      } else {
                                                        warningToast(
                                                          'An error occurred. Please try again later'
                                                        )
                                                      }
                                                      setIsPlanClicked(false)
                                                      setUpgradingPlanId(null)
                                                      // Optionally, show a user-friendly message or perform other actions
                                                    })
                                                } else {
                                                  if (plan?.description === "BILLED_MANUALLY") {
                                                    setShowMsgForCustomPlanUser(true)
                                                  }else{
                                                    const upgradeApi = selectApiForUpgradeWithSubscriptionId(
                                                      userSubscribedPlan?.billing_plan?.billing_frequency
                                                    )
                                                    setIsPlanClicked(true)
                                                    setUpgradingPlanId(plan.id)
                                                    const upgradeRequest =
                                                      upgradeApi === 'request-subscription'
                                                        ? dispatch(
                                                            requestSubscriptionAsync(
                                                              requestSubscriptionPayload(
                                                                plan.id,
                                                                billingPlatformId ?? null
                                                              )
                                                            )
                                                          )
                                                        : dispatch(
                                                            updateSubscriptionAsync(
                                                              changeSubscriptionPayload(
                                                                plan?.id,
                                                                billingPlatformId ?? null,
                                                                currentSubscriptionId
                                                              )
                                                            )
                                                          )
                                                    upgradeRequest
                                                      .unwrap()
                                                      .then((response) => {
                                                        // Check if the response is successful
                                                        if (
                                                          response?.status_code === 201 &&
                                                          response?.data
                                                        ) {
                                                          // successToast(response?.massage)
                                                          window.open(response.data, '_self')
                                                          setTimeout(() => {
                                                            setIsPlanClicked(false)
                                                            setUpgradingPlanId(null)
                                                          }, 4000)
                                                        } else {
                                                          // Handle error or different status codes

                                                          // errorToast(response?.error)
                                                          console.error('Error response:', response)
                                                          warningToast(
                                                            'Something went wrong. Please try again later'
                                                          )
                                                          setIsPlanClicked(false)
                                                          setUpgradingPlanId(null)
                                                          // Optionally, show a user-friendly message or perform other actions
                                                        }
                                                      })
                                                      .catch((error) => {
                                                        // Handle any errors that occur during dispatch or response handling
                                                        console.error(
                                                          'Error during subscription update:',
                                                          error
                                                        )
                                                        if (
                                                          process.env['REACT_APP_ENV'] === 'staging'
                                                        ) {
                                                          // warningToast('An error occurred ' + error?.data?.massage )
                                                          warningToast(error?.data?.massage)
                                                        } else {
                                                          // warningToast('An error occurred. Please try again later')
                                                          warningToast(error?.data?.massage)
                                                        }
                                                        setIsPlanClicked(false)
                                                        setUpgradingPlanId(null)
                                                        // Optionally, show a user-friendly message or perform other actions
                                                      })
                                                  }}
                                              }
                                            }
                                          }}
                                        >
                                          {upgradingPlanId === plan.id && isPlanClicked ? (
                                            <div className='min-w-100px'>
                                              <Spinner animation='border' size='sm' />
                                            </div>
                                          ) : userSubscribedPlan == undefined ||
                                            !userSubscribedPlan?.isActive ? (
                                            'Subscribe'
                                          ) : (
                                            'Upgrade'
                                          )}
                                          </button>,
                                          !isFreePlan
                                            ? getSallaTooltip(sallaAction)
                                            : userSubscribedPlan?.isActive
                                            ? getSallaTooltip(sallaAction)
                                            : undefined
                                        )
                                      ) : (
                                        wrapWithSallaTooltip(
                                          <button
                                            className='btn btn-sm btn-primary shadow px-3 mt-2 fw-bold fs-7 pt-1'
                                            style={{
                                              paddingBottom: '5px',
                                              pointerEvents: isSallaSource ? 'none' : 'auto', // Let parent span handle tooltip hover for Salla
                                            }}
                                            disabled={
                                              isPlanClicked ||
                                              fetchedUserAccountData?.role?.manage_plan_access === "READ" ||
                                              fetchedUserAccountData?.role?.manage_plan_access === "RESTRICT" ||
                                              isSallaSource // Disable downgrade for Salla
                                            }
                                            title={getSallaTooltip('Downgrade')}
                                            onClick={() => {
                                              if (isCustomPlan && shouldUsePaypalForPlan(plan, isCustomPlan)) {
                                                setSelectedPlan(plan)
                                                setIsModalOpen(true)
                                                return
                                              }
                                              if (shouldUsePaypalForSource && plan.billing_frequency !== 'FREE_FOREVER') {
                                                setSelectedPlan(plan)
                                                setIsModalOpen(true)
                                                return
                                              }
                                              if (plan?.description === 'BILLED_MANUALLY') {
                                                setShowMsgForCustomPlanUser(true)
                                              } else {
                                                if (
                                                  ['WIX'].includes(fetchedUserAccountData?.source!)
                                              ) {
                                                if (userSubscribedPlan?.cancelled_at !== null) {
                                                  if (plan.billing_frequency !== 'FREE_FOREVER') {
                                                    setIsPlanClicked(true)
                                                    setDowngradingPlanId(plan.id)
                                                    dispatch(
                                                      requestSubscriptionAsync({
                                                        plan_id: plan.id,
                                                        platform_id: billingPlatformId ?? null,
                                                        // take_free_days_trial: plan.trialPeriodDays,
                                                      })
                                                    )
                                                      .unwrap()
                                                      .then((response) => {
                                                        // Check if the response is successful
                                                        if (
                                                          response.status_code === 201 &&
                                                          response.data
                                                        ) {
                                                          // Automatically open the link in a new tab
                                                          // successToast(response?.massage)
                                                          window.open(response.data, '_self')
                                                          setTimeout(() => {
                                                            setIsPlanClicked(false)
                                                            setDowngradingPlanId(null)
                                                          }, 4000)
                                                        } else {
                                                          // Handle error or different status codes
                                                          console.error('Error response:', response)
                                                          warningToast(
                                                            'Something went wrong. Please try again later'
                                                          )
                                                          setIsPlanClicked(false)
                                                          setDowngradingPlanId(null)
                                                          // Optionally, show a user-friendly message or perform other actions
                                                        }
                                                      })
                                                      .catch((error) => {
                                                        // Handle any errors that occur during dispatch or response handling
                                                        console.error(
                                                          'Error during subscription request:',
                                                          error
                                                        )
                                                        if (
                                                          process.env['REACT_APP_ENV'] === 'staging'
                                                        ) {
                                                          warningToast(
                                                            'An error occurred ' +
                                                              error?.data?.massage
                                                          )
                                                        } else {
                                                          warningToast(
                                                            'U An error occurred. Please try again later'
                                                          )
                                                        }
                                                        setIsPlanClicked(false)
                                                        setDowngradingPlanId(null)
                                                        // Optionally, show a user-friendly message or perform other actions
                                                      })
                                                  } else {
                                                    setSelectedPlanId(plan.plan_id)
                                                    setConfirmDowngradeToFree(true)
                                                  }
                                                } else {
                                                  setIsWixDowngrading(true)
                                                }
                                              } else {
                                                if (!currentSubscriptionId) {
                                                  // Dispatch requestSubscriptionAsync if no subscription_id
                                                  setIsPlanClicked(true)
                                                  setDowngradingPlanId(plan.id)
                                                  dispatch(
                                                    requestSubscriptionAsync({
                                                      plan_id: plan.id,
                                                      platform_id: billingPlatformId ?? null,
                                                      // take_free_days_trial: plan.trialPeriodDays,
                                                    })
                                                  )
                                                    .unwrap()
                                                    .then((response) => {
                                                      // Check if the response is successful
                                                      if (
                                                        response.status_code === 201 &&
                                                        response.data
                                                      ) {
                                                        // Automatically open the link in a new tab
                                                        // successToast(response?.massage)
                                                        window.open(response.data, '_self')
                                                        setTimeout(() => {
                                                          setIsPlanClicked(false)
                                                          setDowngradingPlanId(null)
                                                        }, 4000)
                                                      } else {
                                                        // Handle error or different status codes
                                                        console.error('Error response:', response)
                                                        warningToast(
                                                          'Something went wrong. Please try again later'
                                                        )
                                                        setIsPlanClicked(false)
                                                        setDowngradingPlanId(null)
                                                        // Optionally, show a user-friendly message or perform other actions
                                                      }
                                                    })
                                                    .catch((error) => {
                                                      // Handle any errors that occur during dispatch or response handling
                                                      console.error(
                                                        'Error during subscription request:',
                                                        error
                                                      )
                                                      if (
                                                        process.env['REACT_APP_ENV'] === 'staging'
                                                      ) {
                                                        warningToast(
                                                          'An error occurred ' +
                                                            error?.data?.massage
                                                        )
                                                      } else {
                                                        warningToast(
                                                          'U An error occurred. Please try again later'
                                                        )
                                                      }
                                                      setIsPlanClicked(false)
                                                      setDowngradingPlanId(null)
                                                      // Optionally, show a user-friendly message or perform other actions
                                                    })
                                                } else {
                                                  // Dispatch updateSubscriptionAsync if subscription_id is present
                                                  if (plan?.billing_frequency === 'FREE_FOREVER') {
                                                    setSelectedPlanId(plan.plan_id)
                                                    setConfirmDowngradeToFree(true)
                                                  } else {
                                                    setIsPlanClicked(true)
                                                    setDowngradingPlanId(plan.id)
                                                    dispatch(
                                                      updateSubscriptionAsync({
                                                        plan_id: plan?.id,
                                                        platform_id: Number(billingPlatformId) ?? null,
                                                        subscription_id: currentSubscriptionId,
                                                      })
                                                    )
                                                      .unwrap()
                                                      .then((response) => {
                                                        // Check if the response is successful
                                                        if (
                                                          response.status_code === 201 &&
                                                          response.data
                                                        ) {
                                                          // Automatically open the link in a new tab
                                                          window.open(response.data, '_self')
                                                          setTimeout(() => {
                                                            setIsPlanClicked(false)
                                                            setDowngradingPlanId(null)
                                                          }, 4000)
                                                        } else {
                                                          // Handle error or different status codes
                                                          console.error('Error response:', response)
                                                          warningToast(
                                                            'Something went wrong. Please try again later'
                                                          )
                                                          setIsPlanClicked(false)
                                                          setDowngradingPlanId(null)
                                                          // Optionally, show a user-friendly message or perform other actions
                                                        }
                                                      })
                                                      .catch((error) => {
                                                        // Handle any errors that occur during dispatch or response handling
                                                        console.error(
                                                          'Error during subscription update:',
                                                          error
                                                        )
                                                        if (
                                                          process.env['REACT_APP_ENV'] === 'staging'
                                                        ) {
                                                          warningToast(error?.data?.massage)
                                                        } else {
                                                          warningToast(error?.data?.massage)
                                                        }
                                                        setIsPlanClicked(false)
                                                        setDowngradingPlanId(null)
                                                        // Optionally, show a user-friendly message or perform other actions
                                                      })
                                                  }
                                                }
                                              }
                                            }
                                          }}
                                        >
                                          {downgradingPlanId === plan.id && isPlanClicked ? (
                                            <div className='min-w-100px'>
                                              <Spinner animation='border' size='sm' />
                                            </div>
                                          ) : (
                                            'Downgrade'
                                          )}
                                        </button>,
                                          getSallaTooltip('Downgrade')
                                        )
                                      )
                                    ) : (
                                      <a
                                        href='mailto:support@mycommercium.com'
                                        className='btn btn-sm btn-primary fw-bold fs-6'
                                      >
                                        Contact Us
                                      </a>
                                    )}
                                  </>
                                ) : null}

                                {userSubscribedPlan?.current_billing_plan == plan.id &&
                                userSubscribedPlan?.cancelled_at !== null &&
                                plan?.billing_frequency !== 'FREE_FOREVER' ? (
                                  <>
                                    <button
                                      className='btn btn-sm btn-danger shadow m-2 fw-bold fs-7 pt-1 w-150px'
                                      style={{paddingBottom: '4px'}}
                                      disabled
                                    >
                                      Cancelled
                                    </button>
                                    <p className='fs-8 fw-bold'>
                                      Valid Till{' '}
                                      {userSubscribedPlan?.valid_till
                                        ? formatBillingDateTimeUtc(userSubscribedPlan.valid_till)
                                        : ''}
                                    </p>
                                  </>
                                ) : null}

                                {userSubscribedPlan?.current_billing_plan === plan?.id &&
                                userSubscribedPlan?.isActive && userSubscribedPlan.billing_plan.description !=="BILLED_MANUALLY" &&
                                userSubscribedPlan.cancelled_at === null
                                  ? !planSectionOnly &&
                                    canShowCancelSubscription(plan) && (
                                      <>
                                        {wrapWithSallaTooltip(
                                          <button
                                            className='btn btn-sm btn-danger shadow m-2 fw-bold fs-7 pt-1'
                                            disabled={
                                              isPlanClicked ||
                                              fetchedUserAccountData?.role?.manage_plan_access ===
                                                'READ' ||
                                              fetchedUserAccountData?.role?.manage_plan_access ===
                                                'RESTRICT' ||
                                              isSallaSource
                                            }
                                            title={getSallaTooltip('Cancel Subscription')}
                                            style={{
                                              paddingBottom: '4px',
                                              pointerEvents: isSallaSource ? 'none' : 'auto',
                                            }}
                                            onClick={() => {
                                              if (
                                                ['WIX'].includes(fetchedUserAccountData?.source!)
                                              ) {
                                                setIsWixCancelling(true)
                                              } else {
                                                setIsCancelSubscriptionConfirm(true)
                                              }
                                            }}
                                          >
                                            Cancel Subscription
                                          </button>,
                                          getSallaTooltip('Cancel Subscription')
                                        )}
                                      </>
                                    )
                                  : plan.trialPeriodDays > 0 &&
                                    (userSubscribedPlan?.billing_plan.billing_frequency !==
                                      'FREE_FOREVER' ||
                                      (plan.billing_frequency !== 'FREE_FOREVER' &&
                                        userSubscribedPlan?.activated_on == null)) &&
                                    !['COMMERCIUM'].includes(fetchedUserAccountData?.source!) &&
                                    null}
                              </div> )}
                            </div>
                          </div>
                        </div>
                      )
                    }
                  })
                ) : (
                  <div className='card shadow'>
                    <div className='card-body'>
                      <h3>No Plans Found</h3>
                    </div>
                  </div>
                )}
              </div>
              {!planSectionOnly &&
              !isPlanAndUsageWelcomeUserProp &&
              !(planSectionOnly && isNewFreeTrialOnboardingUser) && (
              <div className='d-flex align-items-center justify-content-center'>
                 {(isWrite && fetchedUserAccountData?.isOnboardingProcessCompleted) ? (
                <Link
                  className={clsx(
                    'btn btn-sm btn-primary align-self-center fs-5 mt-4 mb-3 fw-bold',
                    !fetchedUserAccountData?.is_subscribed ? 'w-200px' : '',
                    (fetchedUserAccountData?.role?.manage_plan_access === "READ" ||
                    fetchedUserAccountData?.role?.manage_plan_access === "RESTRICT")
                      ? 'disabled-link' // Custom disabled style
                      : ''
                  )}
                  to='/listing-channels'
                  onClick={(e) => {
                    if (
                      fetchedUserAccountData?.role?.manage_plan_access === "READ" ||
                      fetchedUserAccountData?.role?.manage_plan_access === "RESTRICT"
                    ) {
                      e.preventDefault(); // Stop navigation
                    }
                  }}
                  style={{
                    pointerEvents:
                      fetchedUserAccountData?.role?.manage_plan_access === "READ" ||
                      fetchedUserAccountData?.role?.manage_plan_access === "RESTRICT"
                        ? 'none'
                        : 'auto',
                    opacity:
                      fetchedUserAccountData?.role?.manage_plan_access === "READ" ||
                      fetchedUserAccountData?.role?.manage_plan_access === "RESTRICT"
                        ? 0.5
                        : 1
                  }}
                >
                  Connect a new Channel or Change settings
                </Link>
              ) : (
                <button
                  className={clsx(
                    'btn btn-sm btn-primary align-self-center fs-5 mt-4 mb-3 fw-bold w-200px disabled'
                  )}
                  disabled
                  style={{ cursor: 'not-allowed', opacity: 0.5 }}
                >
                  Get Started
                </button>
              )}
              </div>
              )}
            </div>
            ) : (
              <Loader />
            )}
          </div>
        </div>
        </>
        )}

        {!planSectionOnly && activeTab === 'TRANSACTIONS' && (
          <InvoiceDetailsGeneration />
        )}
      </div>

      {isModalOpen && (
        <PayPalModal
          selectedPlan={selectedPlan}
          email={fetchedUserAccountData?.email}
          onClose={() => setIsModalOpen(false)}
        />
      )}

      <CommonAlertModal
        title={
          ['WIX'].includes(fetchedUserAccountData?.source!)
            ? 'You have to cancel your current plan from Wix admin portal.'
            : `Are you sure to cancel your subscription? It will remain active until your current plan expires. After that your plan will be downgraded to a Free plan.`
        }
        subtitle=''
        isTermsVisible={false}
        successDisabled={isCanceling}
        successBtnTitle={isCanceling ? 'Updating...' : 'Yes Confirm'}
        isVisible={isCancelSubscriptionConfirm}
        onCancel={() => {
          setIsCancelSubscriptionConfirm(false)
        }}
        onSuccess={() => {
          if (!['WIX'].includes(fetchedUserAccountData?.source!)) {
            setIsCanceling(true)
            dispatch(
              cancelSubscriptionAsync({
                subscription_id: currentSubscriptionId,
                platform_id: billingPlatformId ?? null,
              })
            ).then((response) => {
              if (response?.payload?.status_code === 201 && response?.payload?.data) {
                successToast(response?.payload?.message)
                setIsCanceling(false)
                setIsCancelSubscriptionConfirm(false)
                dispatch(getUserPlanAsync()).then((response) => {
                  if (response.payload?.data?.billing_plan) {
                    setUserSubscriptionPlan({
                      ...response.payload?.data,
                      user_count: response.payload?.user_count,
                    })
                  }
                  if (response.payload?.status === 204) {
                    //will not get user billing plan
                    setUserSubscriptionPlan(undefined)
                    // warningToast(response.payload?.data?.error)
                  }
                  if (response.payload?.status === 500) {
                    warningToast(response.payload?.data?.error)
                  }
                })
              } else {
                errorToast(response?.payload?.message)
                setIsCanceling(false)
                dispatch(getUserPlanAsync()).then((response) => {
                  if (response.payload?.data?.billing_plan) {
                    setUserSubscriptionPlan({
                      ...response.payload?.data,
                      user_count: response.payload?.user_count,
                    })
                  }
                  if (response.payload?.status === 204) {
                    //will not get user billing plan
                    setUserSubscriptionPlan(undefined)
                    // warningToast(response.payload?.data?.error)
                  }
                  if (response.payload?.status === 500) {
                    warningToast(response.payload?.data?.error)
                  }
                })
              }
            })
          } else {
            setIsCancelSubscriptionConfirm(false)
          }
        }}
      />

      {/* downgrade to free plan confirmation popup */}
      <CommonAlertModal
        title={
          userSubscribedPlan == undefined
            ? `Are you sure you want to proceed with the free plan?`
            : `Are you sure you want to downgrade to the Free plan?`
        }
        subtitle=''
        isTermsVisible={false}
        successDisabled={isCanceling}
        successBtnTitle={isCanceling ? 'Updating...' : 'Yes Confirm'}
        isVisible={confirmDowngradeToFree}
        onCancel={() => {
          setConfirmDowngradeToFree(false)
          setSelectedPlanId(null)
        }}
        onSuccess={() => {
          setIsCanceling(true)
          dispatch(requestFreePlanForWoocom({plan_name: selectedPlanId}))
            .unwrap()
            .then((response) => {
              if (response.status_code === 200) {
                // Automatically open the link in a new tab
                successToast(response?.message)
                setIsCanceling(false)
                setIsLoading(true)
                setPlansLoadStatus('loading')
                setLoadedPlanType(null)
                setListOfPlans([])
                Promise.all([
                  dispatch(fetchUserAccountDetailsAsync()),
                  dispatch(getAllPlansAsync({user_id: fetchedUserAccountData?.id!, type: planType})).then(
                    (response) => {
                      if (getAllPlansAsync.fulfilled.match(response) && Array.isArray(response.payload)) {
                        setListOfPlans(response.payload)
                        setLoadedPlanType(planType)
                        setPlansLoadStatus('success')
                      } else {
                        console.error('Expected an array for listOfPlans:', response.payload)
                        setListOfPlans([])
                        setLoadedPlanType(null)
                        setPlansLoadStatus('error')
                      }
                    }
                  ),
                  dispatch(getUserPlanAsync()).then((response) => {
                    if (response.payload?.data?.billing_plan) {
                      setUserSubscriptionPlan({
                        ...response.payload?.data,
                        user_count: response.payload?.user_count,
                      })
                    }
                    if (response.payload?.status === 204) {
                      //will not get user billing plan
                      setUserSubscriptionPlan(undefined)
                      // warningToast(response.payload?.data?.error)
                    }
                    if (response.payload?.status === 500) {
                      warningToast(response.payload?.data?.error)
                    }
                  }),
                ]).finally(() => {
                  setIsLoading(false)
                  dispatch(
                    setStepsData({
                      stepData: {stepOne: 'active', stepTwo: 'inactive', stepThree: 'inactive'},
                      visible: true,
                    })
                  )
                })
              } else {
                // Handle error or different status codes
                setIsLoading(false)
                setIsCanceling(false)
                // Optionally, show a user-friendly message or perform other actions
              }
              setConfirmDowngradeToFree(false)
            })
            .catch((error) => {
              // Handle any errors that occur during dispatch or response handling
              setConfirmDowngradeToFree(false)
              setIsCanceling(false)
              setIsPlanClicked(false)
              console.error('Error during subscription request:', error)
            })
        }}
      />

      <CommonAlertModal
        key={'WIX_USER_ALERT'}
        title={
          isWixDowngrading
            ? 'You need to cancel your current plan first through the Wix admin portal, and then you can choose your desired plan. Wix does not allow customers to downgrade plans through third-party sources.'
            : isWixCancelling
            ? 'Wix does not permit customers to manage their plans through third-party platforms. To cancel your plan, please use the Wix Admin Portal.'
            : ''
        }
        subtitle=''
        isTermsVisible={false}
        successBtnTitle={'Ok'}
        showCloseButton={false}
        isVisible={isWixDowngrading || isWixCancelling}
        onCancel={() => {}}
        onSuccess={() => {
          setIsWixDowngrading(false)
          setIsWixCancelling(false)
        }}
      />

      <CommonAlertModal
        key={'CUSTOM_PLAN_USER_ALERT'}
        title={
          <p className='fs-5'>
            Please contact administrator to Subscribe/Upgrade/Downgrade your plan. To connect with
            administrator <a href='mailto:support@mycommercium.com'>Click Here</a>{' '}
          </p>
        }
        subtitle=''
        isTermsVisible={false}
        successBtnTitle={'Ok'}
        showCloseButton={false}
        isVisible={showMsgForCustomPlanUser}
        onCancel={() => {}}
        onSuccess={() => {
          setShowMsgForCustomPlanUser(false)
        }}
      />
    </>
  )
}

export default UpdatedPricingPageV2
